import React, { useEffect, useState } from "react";
import ReactSelect from "react-select";
import { useForm, Controller } from "react-hook-form";
import moment from "moment";
import { toast } from "react-toastify";
import {
  Eye,
  Trash2,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
  ListFilter,
  Activity,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchDevFeedbackListData,
  fetchDevFeedbackById,
  deleteDevFeedback,
  getDevFeedbackRecords,
  getDevFeedbackTotalRecords,
  getDevFeedbackPage,
  getDevFeedbackSize,
  getDevFeedbackFilter,
  getDevFeedbackLoading,
  setFilter,
  resetFilter,
  setPage,
  setSize,
  openDetailModal,
} from "../../features/devFeedback/devFeedbackSlice";
import { DevFeedbackProps, DevFeedbackFilterType, DevFeedbackStatus, DevFeedbackSeverity } from "../../types/devFeedback";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { filterSelectStyles, toastStyles } from "../../static/styles";
import { t } from "../../locales";
import useConfirmPopup from "../../context/useConfirmPopup";
import DevFeedbackDetailModal from "./DevFeedbackDetailModal";

const listSelectStyles: any = {
  ...filterSelectStyles,
  control: (base: any, state: any) => ({
    ...(typeof filterSelectStyles.control === "function"
      ? (filterSelectStyles.control as any)(base, state)
      : base),
    borderRadius: "0.5rem",
    fontSize: "1rem",
  }),
};

const severityBadgeClass = (severity: DevFeedbackSeverity) => {
  switch (severity) {
    case "bug":
      return "bg-rose-600 hover:bg-rose-700 text-white";
    case "ui":
      return "bg-amber-500 hover:bg-amber-600 text-white";
    case "suggestion":
      return "bg-blue-600 hover:bg-blue-700 text-white";
    default:
      return "bg-gray-500 hover:bg-gray-600 text-white";
  }
};

const statusBadgeClass = (status: DevFeedbackStatus) => {
  switch (status) {
    case "open":
      return "bg-orange-500 hover:bg-orange-600 text-white";
    case "in_progress":
      return "bg-blue-600 hover:bg-blue-700 text-white";
    case "resolved":
      return "bg-emerald-600 hover:bg-emerald-700 text-white";
    default:
      return "bg-gray-500 hover:bg-gray-600 text-white";
  }
};

const DevFeedbackList: React.FC = () => {
  const dispatch = useAppDispatch();
  const labels = t.devFeedback;
  const confirmAction = useConfirmPopup();

  const records = useAppSelector(getDevFeedbackRecords);
  const totalRecords = useAppSelector(getDevFeedbackTotalRecords);
  const page = useAppSelector(getDevFeedbackPage);
  const size = useAppSelector(getDevFeedbackSize);
  const filter = useAppSelector(getDevFeedbackFilter);
  const loading = useAppSelector(getDevFeedbackLoading);

  const severityOptions = [
    { value: "bug", label: labels.severityBug },
    { value: "ui", label: labels.severityUi },
    { value: "suggestion", label: labels.severitySuggestion },
    { value: "other", label: labels.severityOther },
  ];
  const statusOptions = [
    { value: "open", label: labels.statusOpen },
    { value: "in_progress", label: labels.statusInProgress },
    { value: "resolved", label: labels.statusResolved },
    { value: "closed", label: labels.statusClosed },
  ];

  const statusLabel = (status: DevFeedbackStatus) =>
    statusOptions.find((o) => o.value === status)?.label ?? status;
  const severityLabel = (severity: DevFeedbackSeverity) =>
    severityOptions.find((o) => o.value === severity)?.label ?? severity;

  const [showFilters, setShowFilters] = useState(false);

  const { register, control, handleSubmit, reset } = useForm<DevFeedbackFilterType>({
    defaultValues: {
      search: filter.search ?? "",
      status: filter.status ?? null,
      severity: filter.severity ?? null,
      fromDate: filter.fromDate ?? "",
      toDate: filter.toDate ?? "",
    },
  });

  useEffect(() => {
    dispatch(fetchDevFeedbackListData({ page, size, filter }));
  }, [dispatch, page, size, filter]);

  const totalPages = Math.max(1, Math.ceil(totalRecords / size));
  const start = totalRecords === 0 ? 0 : (page - 1) * size + 1;
  const end = Math.min(page * size, totalRecords);

  const getVisiblePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const onApplyFilters = (data: DevFeedbackFilterType) => {
    dispatch(setFilter({
      search: (data.search as any)?.trim() || null,
      status: data.status || null,
      severity: data.severity || null,
      fromDate: data.fromDate || null,
      toDate: data.toDate || null,
    }));
  };

  const handleClearFilters = () => {
    reset({ search: "", status: null, severity: null, fromDate: "", toDate: "" });
    dispatch(resetFilter());
  };

  const onView = (feedback: DevFeedbackProps) => {
    dispatch(openDetailModal(feedback));
    dispatch(fetchDevFeedbackById(feedback.id));
  };

  const onDelete = async (feedback: DevFeedbackProps) => {
    const confirmed = await confirmAction(
      labels.deleteConfirmTitle,
      labels.deleteConfirmMessage,
      labels.deleteConfirmButton
    );
    if (!confirmed) return;

    const action: any = await dispatch(deleteDevFeedback(feedback.id));
    const response = action.payload;
    if (response?.data?.status === "success") {
      toast.success(response.data.message || labels.deleteSuccess, { ...(toastStyles as any) });
    } else {
      toast.error(response?.data?.message || labels.deleteError, { ...(toastStyles as any) });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-3 ml-auto">
          <Button
            variant="outline"
            className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {labels.filterToggle}
          </Button>
        </div>
      </div>

      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{labels.filterTitle}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <form onSubmit={handleSubmit(onApplyFilters)}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {labels.searchLabel}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A]" />
                    <Input
                      className="pl-12 h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand"
                      placeholder={labels.searchPlaceholder}
                      {...register("search")}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {labels.filterStatus}
                  </label>
                  <div className="relative">
                    <ListFilter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={listSelectStyles}
                          options={statusOptions}
                          placeholder={labels.allStatuses}
                          value={statusOptions.find((o) => o.value === field.value) ?? null}
                          onChange={(opt: any) => field.onChange((opt?.value as DevFeedbackStatus) ?? null)}
                          isClearable
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {labels.filterSeverity}
                  </label>
                  <div className="relative">
                    <Activity className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="severity"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={listSelectStyles}
                          options={severityOptions}
                          placeholder={labels.allSeverities}
                          value={severityOptions.find((o) => o.value === field.value) ?? null}
                          onChange={(opt: any) => field.onChange((opt?.value as DevFeedbackSeverity) ?? null)}
                          isClearable
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {labels.filterFromDate}
                  </label>
                  <Input
                    type="date"
                    className="h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand"
                    {...register("fromDate")}
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {labels.filterToDate}
                  </label>
                  <Input
                    type="date"
                    className="h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand"
                    {...register("toDate")}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-4 border-t pt-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm"
                  onClick={handleClearFilters}
                >
                  {labels.clearFilters}
                </Button>
                <Button
                  type="submit"
                  className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm"
                >
                  {labels.applyFilters}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && records.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {labels.loading}
              </div>
            </CardContent>
          </Card>
        ) : !loading && records.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              {labels.noRecords}
            </CardContent>
          </Card>
        ) : (
          <div className={`space-y-3${loading ? " opacity-50 pointer-events-none" : ""}`}>
            {records.map((r, idx) => (
              <Card key={r.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-400">
                        #{(page - 1) * size + idx + 1}
                      </span>
                      <div>
                        <div className="font-semibold text-gray-800 text-sm">{r.page_path}</div>
                        <div className="text-xs text-gray-500 line-clamp-2">{r.message}</div>
                      </div>
                    </div>
                    <Badge className={severityBadgeClass(r.severity)}>{severityLabel(r.severity)}</Badge>
                  </div>

                  <div className="mt-3 pt-3 border-t space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">{labels.colTimestamp}</span>
                      <span className="text-gray-700">
                        {moment(r.created_at).format("DD MMM YYYY, hh:mm A")}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{labels.colUser}</span>
                      <span className="text-gray-700">
                        {r.user?.name ?? labels.systemUser}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">{labels.colStatus}</span>
                      <Badge className={statusBadgeClass(r.status)}>{statusLabel(r.status)}</Badge>
                    </div>
                  </div>

                  <div className="flex justify-end mt-2 pt-2 border-t gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10"
                      onClick={() => onView(r)}
                    >
                      <Eye className="h-4 w-4 text-[#113F67]" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-lg hover:bg-rose-50"
                      onClick={() => onDelete(r)}
                    >
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Desktop table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">{labels.colSr}</TableHead>
                  <TableHead>{labels.colTimestamp}</TableHead>
                  <TableHead>{labels.colUser}</TableHead>
                  <TableHead>{labels.colPage}</TableHead>
                  <TableHead>{labels.colComponent}</TableHead>
                  <TableHead>{labels.colType}</TableHead>
                  <TableHead>{labels.colStatus}</TableHead>
                  <TableHead className="text-right">{labels.colActions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                        {labels.loading}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      {labels.noRecords}
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((r, idx) => (
                    <TableRow key={r.id} className="align-top">
                      <TableCell className="font-medium text-gray-500">
                        {(page - 1) * size + idx + 1}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        <div className="text-gray-700">
                          {moment(r.created_at).format("DD-MM-YYYY")}
                        </div>
                        <div className="text-xs text-gray-400">
                          {moment(r.created_at).format("hh:mm A")}
                        </div>
                      </TableCell>
                      <TableCell>
                        {r.user?.name ?? (
                          <span className="text-gray-400">{labels.systemUser}</span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium text-[#113F67] max-w-[220px] truncate">
                        {r.page_path}
                      </TableCell>
                      <TableCell className="text-gray-600 max-w-[200px] truncate">
                        {r.component_label ?? "-"}
                      </TableCell>
                      <TableCell>
                        <Badge className={severityBadgeClass(r.severity)}>{severityLabel(r.severity)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusBadgeClass(r.status)}>{statusLabel(r.status)}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10"
                            onClick={() => onView(r)}
                          >
                            <Eye className="h-4 w-4 text-[#113F67]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-lg hover:bg-rose-50"
                            onClick={() => onDelete(r)}
                          >
                            <Trash2 className="h-4 w-4 text-rose-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {!loading && records.length > 0 && (
        <div className="flex flex-wrap items-center justify-end md:justify-between gap-4">
          <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
            <span>
              {start}-{end} of {totalRecords}
            </span>
            <span>{labels.pageSizeLabel}</span>
            <Select
              value={String(size)}
              onValueChange={(v) => dispatch(setSize(Number(v)))}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => dispatch(setPage(1))}
              disabled={page === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => dispatch(setPage(Math.max(1, page - 1)))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getVisiblePages().map((p, i) =>
              typeof p === "string" ? (
                <span key={`e-${i}`} className="px-2 text-gray-400">
                  ...
                </span>
              ) : (
                <Button
                  key={p}
                  variant={page === p ? "default" : "outline"}
                  className={`w-8 h-8 p-0 ${page === p ? "bg-[#113F67] hover:bg-[#0d2f4d]" : ""}`}
                  onClick={() => dispatch(setPage(p))}
                >
                  {p}
                </Button>
              )
            )}
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => dispatch(setPage(Math.min(totalPages, page + 1)))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => dispatch(setPage(totalPages))}
              disabled={page === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <DevFeedbackDetailModal />
    </div>
  );
};

export default DevFeedbackList;
